import * as THREE from 'three';

type QuietLandPreviewKind = 'distant' | 'frontier' | 'recommended' | 'owned';
export type LockedPlotPreviewKind = 'distant' | 'frontier' | 'recommended';

/** Marker scale only. Perimeter stays the exact atomic footprint. */
export const LOCKED_SALE_MARKER_SCALE: Record<LockedPlotPreviewKind, number> = {
  distant: 1,
  frontier: 1.18,
  recommended: 1.62,
};

/** COL-010-C2 values kept as the readability baseline. Too faint against grassQuiet. */
export const C2_DISTANT_TINT = 0x9ec47a;
export const C2_DISTANT_TINT_OPACITY = 0.16;
export const C2_DISTANT_BORDER = 0xc5d4b8;
export const C2_DISTANT_BORDER_OPACITY = 0.28;
export const C2_OWNED_TINT = 0xc4b46a;
export const C2_OWNED_TINT_OPACITY = 0.2;
export const C2_OWNED_BORDER = 0xdfd2a0;
export const C2_OWNED_BORDER_OPACITY = 0.34;

/**
 * COL-010-C3: materially more legible than C2. Distant uses a lighter sage wash
 * so it no longer matches grassQuiet. Owned stays warmer/gold-green.
 * COL-010-C7 adds one compact wooden sale marker on locked plots.
 * COL-010-C8: locked plots reuse the established PARK_V1 sale red
 * (`mats.rope` / 0xe0504f) as a clean perimeter line. No stakes/rope mesh.
 * COL-010-C9: every still-locked atomic plot uses this exact-footprint path.
 */
export const SALE_PERIMETER_RED = 0xe0504f;
export const DISTANT_TINT = 0xc3d6a8;
export const DISTANT_TINT_OPACITY = 0.42;
export const DISTANT_BORDER = SALE_PERIMETER_RED;
export const DISTANT_BORDER_OPACITY = 0.82;
/** Stronger existing-vocabulary wash so Find-land targets are unmistakable. */
export const RECOMMENDED_TINT = 0xf3c24a;
export const RECOMMENDED_TINT_OPACITY = 0.62;
export const RECOMMENDED_BORDER = SALE_PERIMETER_RED;
export const RECOMMENDED_BORDER_OPACITY = 0.96;
export const OWNED_TINT = 0xd2c078;
export const OWNED_TINT_OPACITY = 0.36;
export const OWNED_BORDER = 0x6e5f34;
export const OWNED_BORDER_OPACITY = 0.7;

/** Opaque pad + red rope + wooden sign in createLockedParcels. Quiet overlays stay below this. */
export const FRONTIER_VISUAL_WEIGHT = 3;

/** Same swatches as `createLockedParcels` wood post / board / cream face. */
export const SALE_MARKER_WOOD_DARK = 0x8b5a34;
export const SALE_MARKER_WOOD = 0xc98a52;
export const SALE_MARKER_CREAM = 0xfff6e6;

/** Frontier locked-parcel wooden sign (terrain.ts createLockedParcels). Distant is the same vocabulary, smaller. */
export const FRONTIER_SALE_SIGN = {
  post: { w: 0.06, h: 0.62, d: 0.06 },
  board: { w: 0.62, h: 0.34, d: 0.05 },
  face: { w: 0.5, h: 0.2, d: 0.02 },
} as const;

export const DISTANT_SALE_SIGN = {
  post: { w: 0.04, h: 0.4, d: 0.04 },
  board: { w: 0.4, h: 0.22, d: 0.035 },
  face: { w: 0.32, h: 0.13, d: 0.015 },
} as const;

export interface QuietPreviewMaterials {
  tintColor: number;
  tintOpacity: number;
  borderColor: number;
  borderOpacity: number;
}

export function quietPreviewProminence(materials: Pick<QuietPreviewMaterials, 'tintOpacity' | 'borderOpacity'>): number {
  return materials.tintOpacity + materials.borderOpacity;
}

export function quietPreviewMaterials(group: THREE.Group): QuietPreviewMaterials {
  const tint = group.children[0] as THREE.Mesh;
  const border = group.children[1] as THREE.Line;
  return {
    tintColor: (tint.material as THREE.MeshBasicMaterial).color.getHex(),
    tintOpacity: (tint.material as THREE.MeshBasicMaterial).opacity,
    borderColor: (border.material as THREE.LineBasicMaterial).color.getHex(),
    borderOpacity: (border.material as THREE.LineBasicMaterial).opacity,
  };
}

function createQuietIslandPlotPreview(
  land: { x: number; z: number; width: number; depth: number },
  parkTop: number,
  kind: QuietLandPreviewKind,
  tintColor: number,
  tintOpacity: number,
  borderColor: number,
  borderOpacity: number,
): THREE.Group {
  const group = new THREE.Group();
  group.userData.landPreview = kind;
  const width = Math.max(land.width, 0.8) * 0.96;
  const depth = Math.max(land.depth, 0.8) * 0.96;
  const tint = new THREE.Mesh(
    new THREE.PlaneGeometry(width, depth),
    new THREE.MeshBasicMaterial({
      color: tintColor,
      transparent: true,
      opacity: tintOpacity,
      depthWrite: false,
    }),
  );
  tint.rotation.x = -Math.PI / 2;
  tint.position.set(land.x, parkTop + 0.014, land.z);
  tint.raycast = () => undefined;
  group.add(tint);

  const halfW = width / 2;
  const halfD = depth / 2;
  const border = new THREE.Line(
    new THREE.BufferGeometry().setFromPoints([
      new THREE.Vector3(-halfW, 0, -halfD),
      new THREE.Vector3(halfW, 0, -halfD),
      new THREE.Vector3(halfW, 0, halfD),
      new THREE.Vector3(-halfW, 0, halfD),
      new THREE.Vector3(-halfW, 0, -halfD),
    ]),
    new THREE.LineBasicMaterial({
      color: borderColor,
      transparent: true,
      opacity: borderOpacity,
      depthWrite: false,
    }),
  );
  border.position.set(land.x, parkTop + 0.024, land.z);
  border.raycast = () => undefined;
  if (kind !== 'owned') {
    border.userData.landSalePerimeter = true;
  }
  group.add(border);
  return group;
}

function disableRaycast(object: THREE.Object3D): void {
  object.raycast = () => undefined;
  for (const child of object.children) {
    disableRaycast(child);
  }
}

function saleMarkerMaterial(color: number): THREE.MeshBasicMaterial {
  return new THREE.MeshBasicMaterial({
    color,
    depthWrite: true,
  });
}

/** Compact wooden sale sign: same post/board/cream language as createLockedParcels, quieter and sign-only. */
export function createDistantSaleMarker(parkTop: number): THREE.Group {
  const marker = new THREE.Group();
  marker.name = 'landSaleMarker';
  marker.userData.landSaleMarker = true;
  const post = new THREE.Mesh(
    new THREE.BoxGeometry(DISTANT_SALE_SIGN.post.w, DISTANT_SALE_SIGN.post.h, DISTANT_SALE_SIGN.post.d),
    saleMarkerMaterial(SALE_MARKER_WOOD_DARK),
  );
  post.position.set(0, DISTANT_SALE_SIGN.post.h / 2, 0);
  const board = new THREE.Mesh(
    new THREE.BoxGeometry(DISTANT_SALE_SIGN.board.w, DISTANT_SALE_SIGN.board.h, DISTANT_SALE_SIGN.board.d),
    saleMarkerMaterial(SALE_MARKER_WOOD),
  );
  board.position.set(0, DISTANT_SALE_SIGN.post.h + DISTANT_SALE_SIGN.board.h / 2 - 0.04, 0);
  const face = new THREE.Mesh(
    new THREE.BoxGeometry(DISTANT_SALE_SIGN.face.w, DISTANT_SALE_SIGN.face.h, DISTANT_SALE_SIGN.face.d),
    saleMarkerMaterial(SALE_MARKER_CREAM),
  );
  face.position.set(0, board.position.y, DISTANT_SALE_SIGN.board.d / 2 + DISTANT_SALE_SIGN.face.d / 2);
  marker.add(post, board, face);
  marker.position.y = parkTop + 0.04;
  disableRaycast(marker);
  return marker;
}

export function collectLandSaleMarkers(root: THREE.Object3D): THREE.Object3D[] {
  const found: THREE.Object3D[] = [];
  root.traverse((object) => {
    if (object.userData.landSaleMarker === true) {
      found.push(object);
    }
  });
  return found;
}

export function collectLandSalePerimeters(root: THREE.Object3D): THREE.Line[] {
  const found: THREE.Line[] = [];
  root.traverse((object) => {
    if (object.userData.landSalePerimeter === true && object instanceof THREE.Line) {
      found.push(object);
    }
  });
  return found;
}

/** Exact-footprint Land-mode overlay for every still-locked atomic plot. Compact wooden sale marker + red outline; no stakes or rope. */
export function createDistantIslandPlotPreview(
  land: { x: number; z: number; width: number; depth: number },
  parkTop: number,
  kind: LockedPlotPreviewKind = 'distant',
): THREE.Group {
  const recommended = kind === 'recommended';
  const group = createQuietIslandPlotPreview(
    land,
    parkTop,
    kind,
    recommended ? RECOMMENDED_TINT : DISTANT_TINT,
    recommended ? RECOMMENDED_TINT_OPACITY : DISTANT_TINT_OPACITY,
    recommended ? RECOMMENDED_BORDER : DISTANT_BORDER,
    recommended ? RECOMMENDED_BORDER_OPACITY : DISTANT_BORDER_OPACITY,
  );
  group.userData.landPreview = kind;
  const marker = createDistantSaleMarker(parkTop);
  marker.scale.setScalar(LOCKED_SALE_MARKER_SCALE[kind]);
  marker.position.set(land.x, parkTop + 0.04, land.z);
  group.add(marker);
  return group;
}

/** Quiet Land-mode footprint for already-owned plots. Distinct from distant; still signless. */
export function createOwnedIslandPlotPreview(
  land: { x: number; z: number; width: number; depth: number },
  parkTop: number,
): THREE.Group {
  const group = createQuietIslandPlotPreview(
    land,
    parkTop,
    'owned',
    OWNED_TINT,
    OWNED_TINT_OPACITY,
    OWNED_BORDER,
    OWNED_BORDER_OPACITY,
  );
  group.userData.landPreview = 'owned';
  return group;
}
